import { useState, useEffect, useCallback } from "react";
import {
  Dialog, DialogContent, DialogActions, Box, Stack, Button, IconButton,
  Typography, CircularProgress, Alert, Pagination,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { useTranslation } from "react-i18next";
import { getCustomerAccountByAdmin, getAccountTransactions } from "api/wallet";
import WalletTransactionAdminDetailDialog from "components/WalletTransactionAdminDetailDialog";
import { formatWalletTxnDetail } from "utils/formatWalletTxnDetail";

const MONO = "'Space Grotesk', 'Noto Sans Lao', ui-monospace, monospace";
const CURRENCIES = ["THB", "LAK", "USD"];
const CUR_SYM = { THB: "฿", LAK: "₭", USD: "$" };
const money = (n, c) => `${CUR_SYM[c] || ""}${(Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const fmtD = (d) => (d ? new Date(d).toLocaleDateString() : "");
const fmtT = (d) => (d ? new Date(d).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "");
const ARROW_OUT = "M6 10V2M2 6l4-4 4 4";
const ARROW_IN = "M6 2v8M2 6l4 4 4-4";

function typeStyle(purpose, txnType) {
  if (txnType === "debit") return { bg: "#fff1f2", border: "#fca5a5", col: "#c0343a", arrow: ARROW_OUT };
  if (purpose === "exchange") return { bg: "#f0f9ff", border: "#7dd3fc", col: "#0369a1", arrow: ARROW_IN };
  return { bg: "#f0fdf4", border: "#86efac", col: "#15803d", arrow: ARROW_IN };
}

// Quick-glance wallet history for a customer, opened from the pickup/delivery
// collection screens (via WalletDebtBadge) without leaving the pickup flow.
// Pagination only — no filters, unlike the full scenes/wallet/accountTransactions.jsx
// page, to keep this a fast lookup during a hand-over.
export default function WalletHistoryDialog({ open, onClose, customerId, customerName }) {
  const { t } = useTranslation();
  const theme = useTheme();

  const [account, setAccount] = useState(null);
  const [accountLoading, setAccountLoading] = useState(false);

  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(1);

  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedTxId, setSelectedTxId] = useState(null);

  useEffect(() => {
    if (!open || !customerId) {
      setAccount(null);
      return;
    }
    let active = true;
    setAccountLoading(true);
    setCurrentPage(1);
    getCustomerAccountByAdmin(customerId)
      .then((res) => {
        if (!active) return;
        const found = res && res.success && res.data;
        setAccount(found ? { ...res.data, walletActive: !!res.walletActive } : null);
      })
      .catch(() => {
        if (active) setAccount(null);
      })
      .finally(() => active && setAccountLoading(false));
    return () => {
      active = false;
    };
  }, [open, customerId]);

  const loadTxns = useCallback(
    async (page = 1) => {
      if (!account?._id) return;
      try {
        setLoading(true);
        setError("");
        const result = await getAccountTransactions(account._id, page, 20);
        setTxns(result.data.transactions);
        setPagination(result.data.pagination);
      } catch (err) {
        setError(err.message || t("common.error"));
      } finally {
        setLoading(false);
      }
    },
    [account?._id, t]
  );

  useEffect(() => {
    if (account?._id) loadTxns(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account?._id, currentPage]);

  const openDetail = (tx) => {
    setSelectedTxId(tx.id);
    setDetailOpen(true);
  };

  return (
    <Dialog key={customerId} open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "22px" } }}>
      <DialogContent sx={{ p: { xs: 2.25, sm: 3 } }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 2 }}>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: 19 }} color="text.primary">
              {t("pickup.selfPickup.walletHistory.title")}
            </Typography>
            <Typography sx={{ fontSize: 12.5, mt: 0.25 }} color="text.secondary">
              {customerName || t("pickup.selfPickup.walletHistory.subtitle")}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose}>
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </Stack>

        {accountLoading && (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress color="secondary" size={28} />
          </Box>
        )}

        {!accountLoading && !account && (
          <Alert severity="info" sx={{ borderRadius: "12px" }}>
            {t("pickup.selfPickup.walletHistory.noAccount")}
          </Alert>
        )}

        {!accountLoading && account && (
          <>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              {CURRENCIES.map((c) => {
                const negative = Number(account[c]) < 0;
                return (
                  <Box
                    key={c}
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      borderRadius: "14px",
                      border: `1px solid ${negative ? alpha(theme.palette.error.main, 0.4) : theme.palette.divider}`,
                      background: negative ? alpha(theme.palette.error.main, 0.08) : theme.palette.background.paper,
                      px: 1.25,
                      py: 1,
                    }}
                  >
                    <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".05em" }} color="text.secondary">
                      {c}
                    </Typography>
                    <Typography
                      sx={{ fontFamily: MONO, fontWeight: 800, fontSize: 15, mt: 0.25 }}
                      color={negative ? "error.main" : "text.primary"}
                    >
                      {money(account[c], c)}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>

            {account.walletActive === false && (
              <Alert severity="warning" sx={{ mb: 2, borderRadius: "12px" }}>
                {t("pickup.selfPickup.walletHistory.walletInactiveNote")}
              </Alert>
            )}

            {loading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress color="secondary" />
              </Box>
            ) : error ? (
              <Alert severity="error" sx={{ borderRadius: "12px" }}>{error}</Alert>
            ) : txns.length === 0 ? (
              <Box sx={{ py: 4, textAlign: "center" }}>
                <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                  {t("wallet.admin.noTransactions")}
                </Typography>
              </Box>
            ) : (
              <Stack spacing={1}>
                {txns.map((tx) => {
                  const s = typeStyle(tx.purpose, tx.txn_type);
                  const out = tx.txn_type === "debit";
                  return (
                    <Box
                      key={tx._id}
                      onClick={() => openDetail(tx)}
                      sx={{
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: "14px",
                        p: "12px 14px",
                        cursor: "pointer",
                        "&:hover": { background: alpha(theme.palette.secondary.main, 0.04) },
                      }}
                    >
                      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "5px",
                            fontSize: 11.5,
                            fontWeight: 700,
                            padding: "4px 10px",
                            borderRadius: "999px",
                            border: `1.5px solid ${s.border}`,
                            background: s.bg,
                            color: s.col,
                            textTransform: "capitalize",
                          }}
                        >
                          <Box component="svg" width="10" height="10" viewBox="0 0 12 12" fill="none">
                            <path d={s.arrow} stroke={s.col} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </Box>
                          {tx.purpose}
                        </Box>
                        <Typography sx={{ fontFamily: MONO, fontSize: 15, fontWeight: 700 }} color={out ? "error.main" : "success.main"}>
                          {out ? "-" : "+"}{money(tx.amount, tx.currency)}
                        </Typography>
                      </Stack>
                      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 0.75 }}>
                        <Typography sx={{ fontSize: 11 }} color="text.secondary">
                          {fmtD(tx.createdAt)} · {fmtT(tx.createdAt)}
                        </Typography>
                        <Typography sx={{ fontSize: 11 }} color="text.secondary">
                          {t("wallet.admin.balanceAfter")}: {money(tx.balanceAfter, tx.currency)}
                        </Typography>
                      </Stack>
                      {formatWalletTxnDetail(tx, t) && (
                        <Typography sx={{ fontSize: 11, mt: 0.5 }} color="text.secondary" noWrap>
                          {formatWalletTxnDetail(tx, t)}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Stack>
            )}

            {pagination.pages > 1 && (
              <Box display="flex" justifyContent="center" mt={2}>
                <Pagination
                  count={pagination.pages}
                  page={currentPage}
                  onChange={(_, p) => setCurrentPage(p)}
                  color="secondary"
                  size="small"
                />
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: { xs: 2.25, sm: 3 }, pb: 2.5, pt: 0 }}>
        <Button variant="outlined" color="secondary" onClick={onClose} sx={{ borderRadius: "12px", fontWeight: 700, px: 2.5 }}>
          {t("common.close")}
        </Button>
      </DialogActions>

      <WalletTransactionAdminDetailDialog open={detailOpen} onClose={() => setDetailOpen(false)} txId={selectedTxId} />
    </Dialog>
  );
}
